import { request } from '@/api/apiUtils';
import type { EntityDefinition, FetcherEntry } from '@/data/types';
import type { EditorScenesTextAtomsResponse, ISceneEpisodicPresentation, IScenePresentationDuration, ResponseType } from '@/types';

/** Root: query-key prefix for this entity's slice of the cache (ADR 0010) */
export const SCENES_ROOT = ['scenes'] as const;

interface ScenePresentationsParams {
    project_id: number;
    episode_id: number;
    version?: string;
    act?: number;
    tags?: string;
}

interface SceneTextAtomsParams {
    project_id: number;
    episode_id: number;
    scenes: string;
    version?: string;
}

interface SceneEpisodicPresentationsParams {
    project_id: number;
    episode_id: number;
    version?: string;
    tags?: string;
}

const fetchers = {
    presentations: {
        key: (args: ScenePresentationsParams) => [...SCENES_ROOT, 'presentations', args] as const,
        fetch: async (args: ScenePresentationsParams): Promise<IScenePresentationDuration[]> => {
            const params = new URLSearchParams();
            if (args.act) params.append('act', args.act.toString());
            if (args.tags) params.append('tags', args.tags);
            if (args.version) params.append('version', args.version);
            const endpoint = `projects/${args.project_id}/presentations/editor/scenes/${args.episode_id}${
                params.toString() ? `?${params}` : ''
            }`;
            const data: { data: IScenePresentationDuration[] } = await request(endpoint);
            return data.data;
        },
    },
    textAtoms: {
        key: (args: SceneTextAtomsParams) => [...SCENES_ROOT, 'textAtoms', args] as const,
        fetch: async (args: SceneTextAtomsParams): Promise<EditorScenesTextAtomsResponse[]> => {
            const params = new URLSearchParams();
            params.append('scenes', `[${args.scenes}]`);
            if (args.version) params.append('version', args.version);
            const endpoint = `projects/${args.project_id}/presentations/editor/scenes/${args.episode_id}/text-atoms?${params}`;
            return await request(endpoint);
        },
    },
    episodicPresentations: {
        key: (args: SceneEpisodicPresentationsParams) => [...SCENES_ROOT, 'episodicPresentations', args] as const,
        fetch: async (args: SceneEpisodicPresentationsParams): Promise<ISceneEpisodicPresentation[]> => {
            const params = new URLSearchParams();
            params.append('episode_id', args.episode_id.toString());
            if (args.version) params.append('version', args.version);
            const tagsQuery = args.tags ? args.tags : '';
            const endpoint = `projects/${args.project_id}/presentations/scenes/episodic-plan?${tagsQuery}`;
            const url = `${endpoint}${tagsQuery ? '&' : ''}${params.toString()}`;
            const response: ResponseType<ISceneEpisodicPresentation[]> = await request(url);
            return response.data;
        },
    },
    // biome-ignore lint/suspicious/noExplicitAny: required for generic type inference
} satisfies Record<string, FetcherEntry<any, any>>;

export const scenesEntity: EntityDefinition<typeof fetchers> = {
    name: 'scenes',
    roots: [SCENES_ROOT],
    fetchers,
};

export type { SceneEpisodicPresentationsParams, ScenePresentationsParams, SceneTextAtomsParams };
